import { clientEnvVars } from '../environment/clientEnvVars';

const LOAD_TIMEOUT_MS = 10000;
const CHECK_INTERVAL_MS = 100;

let loadPromise: Promise<void> | null = null;

/**
 * Checks if Google Maps API is loaded and ready
 */
const isLoaded = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof window.google !== 'undefined' &&
    typeof window.google.maps !== 'undefined' &&
    typeof window.google.maps.places !== 'undefined'
  );
};

/**
 * Waits for Google Maps API to be available using Promise.race
 * More efficient than setInterval polling - resolves immediately when API is ready
 */
const waitForInitialization = (): Promise<void> => {
  if (isLoaded()) {
    return Promise.resolve();
  }

  let timeoutId: NodeJS.Timeout | null = null;
  let timeoutHandle: NodeJS.Timeout | null = null;

  const timeoutPromise = new Promise<void>((_, reject) => {
    timeoutHandle = setTimeout(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      reject(
        new Error('Google Maps API failed to initialize within timeout period'),
      );
    }, LOAD_TIMEOUT_MS);
  });

  const readyPromise = new Promise<void>(resolve => {
    const checkReady = (): void => {
      if (isLoaded()) {
        if (timeoutHandle) {
          clearTimeout(timeoutHandle);
        }
        resolve();
        return;
      }
      timeoutId = setTimeout(checkReady, CHECK_INTERVAL_MS);
    };
    checkReady();
  });

  return Promise.race([readyPromise, timeoutPromise]).finally(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }
  });
};

/**
 * Gets Google Places API key from clientEnvVars (Doppler in prod / .env in dev) or window (for local override)
 */
const getGooglePlacesApiKey = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const envKey = clientEnvVars()?.GOOGLE_PLACES_API_KEY;
  if (envKey && typeof envKey === 'string' && envKey.trim()) {
    return envKey.trim();
  }

  const localKey = (window as any).__GOOGLE_PLACES_API_KEY__;
  if (localKey && typeof localKey === 'string' && localKey.trim()) {
    return localKey.trim();
  }

  return null;
};

/**
 * Dynamically loads Google Maps JavaScript API script
 * @returns Promise that resolves when the script is loaded and initialized
 * @throws Error if API key is missing or script fails to load
 */
export const loadGoogleMapsScript = (): Promise<void> => {
  if (loadPromise) {
    return loadPromise;
  }

  if (isLoaded()) {
    return Promise.resolve();
  }

  const existingScript = document.querySelector(
    'script[src*="maps.googleapis.com/maps/api/js"]',
  );
  if (existingScript) {
    loadPromise = waitForInitialization();
    return loadPromise;
  }

  const apiKey = getGooglePlacesApiKey();

  if (!apiKey) {
    return Promise.reject(
      new Error(
        'Google Places API key is not configured. ' +
          'Set GOOGLE_PLACES_API_KEY in Doppler (prod) or in .env / doppler run (local). ' +
          'For quick local testing, set window.__GOOGLE_PLACES_API_KEY__ = "YOUR_API_KEY" in browser console.',
      ),
    );
  }

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      waitForInitialization().then(resolve).catch(reject);
    };

    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load Google Maps JavaScript API script'));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
};
